/**
 * Dots and Boxes Game Logic
 * Grid-based game where players draw lines between dots to complete boxes
 */

/**
 * Creates the initial game state
 * @param {string} hostId - Host player ID
 * @param {string} guestId - Guest player ID
 * @param {number} rows - Number of rows (default 7 for 6x6 boxes)
 * @param {number} cols - Number of columns (default 7 for 6x6 boxes)
 * @returns {Object} Initial game state
 */
export const createInitialGameState = (hostId, guestId, rows = 7, cols = 7) => {
  // Create horizontal lines array (rows x (cols-1) lines)
  // Use empty string instead of null to prevent Firebase from converting arrays to objects
  const horizontalLines = Array(rows).fill(null).map(() =>
    Array(cols - 1).fill('')
  );

  // Create vertical lines array ((rows-1) x cols lines)
  const verticalLines = Array(rows - 1).fill(null).map(() =>
    Array(cols).fill('')
  );

  // Create boxes array ((rows-1) x (cols-1) boxes)
  const boxes = Array(rows - 1).fill(null).map(() =>
    Array(cols - 1).fill('')
  );

  // Randomly decide who goes first
  const firstPlayer = Math.random() < 0.5 ? hostId : guestId;

  return {
    rows,
    cols,
    horizontalLines,
    verticalLines,
    boxes,
    currentTurn: firstPlayer,
    hostId,
    guestId,
    hostScore: 0,
    guestScore: 0,
    isGameOver: false,
    winner: null,
    moveCount: 0,
  };
};

/**
 * Check if a line can be drawn
 * @param {Object} gameState - Current game state
 * @param {string} type - 'horizontal' or 'vertical'
 * @param {number} row - Row index
 * @param {number} col - Column index
 * @returns {boolean} True if line can be drawn
 */
export const isValidMove = (gameState, type, row, col) => {
  if (type === 'horizontal') {
    if (row < 0 || row >= gameState.rows || col < 0 || col >= gameState.cols - 1) {
      return false;
    }
    return !gameState.horizontalLines[row][col];
  } else {
    if (row < 0 || row >= gameState.rows - 1 || col < 0 || col >= gameState.cols) {
      return false;
    }
    return !gameState.verticalLines[row][col];
  }
};

/**
 * Draw a line and check for completed boxes
 * @param {Object} gameState - Current game state
 * @param {string} type - 'horizontal' or 'vertical'
 * @param {number} row - Row index
 * @param {number} col - Column index
 * @param {string} playerId - Player making the move
 * @returns {Object} Updated game state
 */
export const makeMove = (gameState, type, row, col, playerId) => {
  const newState = JSON.parse(JSON.stringify(gameState));

  // Draw the line
  if (type === 'horizontal') {
    newState.horizontalLines[row][col] = playerId;
  } else {
    newState.verticalLines[row][col] = playerId;
  }

  newState.moveCount++;

  // Check for completed boxes
  const completedBoxes = getCompletedBoxes(newState, type, row, col, playerId);

  // Update scores
  if (playerId === newState.hostId) {
    newState.hostScore += completedBoxes.length;
  } else {
    newState.guestScore += completedBoxes.length;
  }

  // If player completed a box, they get another turn
  if (completedBoxes.length > 0) {
    newState.currentTurn = playerId;
  } else {
    // Switch turns
    newState.currentTurn = playerId === newState.hostId ? newState.guestId : newState.hostId;
  }

  // Check if game is over
  const totalBoxes = (newState.rows - 1) * (newState.cols - 1);
  if (newState.hostScore + newState.guestScore === totalBoxes) {
    newState.isGameOver = true;
    if (newState.hostScore > newState.guestScore) {
      newState.winner = newState.hostId;
    } else if (newState.guestScore > newState.hostScore) {
      newState.winner = newState.guestId;
    } else {
      newState.winner = 'draw';
    }
  }

  return newState;
};

/**
 * Get boxes that were completed by the last move
 * @param {Object} gameState - Current game state
 * @param {string} type - 'horizontal' or 'vertical'
 * @param {number} row - Row index
 * @param {number} col - Column index
 * @param {string} playerId - Player who made the move
 * @returns {Array} Array of completed box coordinates
 */
const getCompletedBoxes = (gameState, type, row, col, playerId) => {
  const completed = [];

  if (type === 'horizontal') {
    // Check box above
    if (row > 0) {
      const boxRow = row - 1;
      const boxCol = col;
      if (isBoxComplete(gameState, boxRow, boxCol)) {
        gameState.boxes[boxRow][boxCol] = playerId;
        completed.push({ row: boxRow, col: boxCol });
      }
    }
    // Check box below
    if (row < gameState.rows - 1) {
      const boxRow = row;
      const boxCol = col;
      if (isBoxComplete(gameState, boxRow, boxCol)) {
        gameState.boxes[boxRow][boxCol] = playerId;
        completed.push({ row: boxRow, col: boxCol });
      }
    }
  } else {
    // Check box to the left
    if (col > 0) {
      const boxRow = row;
      const boxCol = col - 1;
      if (isBoxComplete(gameState, boxRow, boxCol)) {
        gameState.boxes[boxRow][boxCol] = playerId;
        completed.push({ row: boxRow, col: boxCol });
      }
    }
    // Check box to the right
    if (col < gameState.cols - 1) {
      const boxRow = row;
      const boxCol = col;
      if (isBoxComplete(gameState, boxRow, boxCol)) {
        gameState.boxes[boxRow][boxCol] = playerId;
        completed.push({ row: boxRow, col: boxCol });
      }
    }
  }

  return completed;
};

/**
 * Check if a box is complete (all 4 sides drawn)
 * @param {Object} gameState - Current game state
 * @param {number} boxRow - Box row index
 * @param {number} boxCol - Box column index
 * @returns {boolean} True if box is complete
 */
const isBoxComplete = (gameState, boxRow, boxCol) => {
  // Check if box is already claimed
  if (gameState.boxes[boxRow][boxCol]) {
    return false;
  }

  // Check all 4 sides
  const top = gameState.horizontalLines[boxRow][boxCol];
  const bottom = gameState.horizontalLines[boxRow + 1][boxCol];
  const left = gameState.verticalLines[boxRow][boxCol];
  const right = gameState.verticalLines[boxRow][boxCol + 1];

  return top && bottom && left && right;
};

/**
 * Get the next turn player ID
 * @param {string} currentTurn - Current player ID
 * @param {string} hostId - Host player ID
 * @param {string} guestId - Guest player ID
 * @returns {string} Next player ID
 */
export const getNextTurn = (currentTurn, hostId, guestId) => {
  return currentTurn === hostId ? guestId : hostId;
};

/**
 * Check game status
 * @param {Object} gameState - Current game state
 * @returns {Object} Status object with isOver, winner, isDraw
 */
export const getGameStatus = (gameState) => {
  const totalBoxes = (gameState.rows - 1) * (gameState.cols - 1);
  const scoredBoxes = gameState.hostScore + gameState.guestScore;

  const isOver = scoredBoxes === totalBoxes;
  let winner = null;
  let isDraw = false;

  if (isOver) {
    if (gameState.hostScore > gameState.guestScore) {
      winner = gameState.hostId;
    } else if (gameState.guestScore > gameState.hostScore) {
      winner = gameState.guestId;
    } else {
      isDraw = true;
    }
  }

  return {
    isOver,
    winner,
    isDraw,
  };
};
